<template>
	<div class="tab-component" :class="classes">
		<!-- Видимые вкладки -->
		<button
			v-for="tab in visibleTabs"
			:key="'tab_' + tab[tabIdentity]"
			class="tab-item"
			:class="{ active: tab.active }"
			@click="changeTab(tab)"
		>
			<slot v-bind:tab="tab">
				<span>{{ tab.title }}</span>
			</slot>
		</button>

		<!-- Скрытые вкладки -->
		<template v-if="hiddenTabs.length > 0">
			<button
				:id="'more_' + uid"
				class="tab-item"
				:class="{
					active: activeHiddenTab !== null,
					'popup-visible': hiddenTabsPopupVisible,
				}"
				@mouseenter="openHiddenTabsPopup"
				@mouseleave="closeHiddenTabsPopup"
			>
				<VIcon class="arrow" material="keyboard_arrow_down" solid />
				<VIcon v-if="activeHiddenTab === null" material="dehaze" solid />
				<template v-else>
					{{ activeHiddenTab[tabTitle] }}
				</template>
			</button>

			<VPopup
				ref="hiddenTabsPopup"
				class="hidden-tabs-popup"
				:target="hiddenTabsPopupTarget"
				:visible="hiddenTabsPopupVisible"
				:offset="-2"
				:align="popupAlign"
				@mouseleave="closeHiddenTabsPopup"
				@close="closeHiddenTabsPopup"
			>
				<button
					v-for="tab in hiddenTabs"
					:key="'tab_' + tab[tabIdentity]"
					class="tab-item"
					:class="{ active: tab.active }"
					@click="changeTab(tab)"
				>
					<slot v-bind:tab="tab">
						<span>{{ tab.title }}</span>
					</slot>
				</button>
			</VPopup>
		</template>
	</div>
</template>

<style lang="scss" src="./VTabs.scss" />

<script>

import Utils from "@/core/utils/Utils"

import VIcon from "@/lib/components/VIcon/VIcon";
import VPopup from "@/lib/components/VPopup/VPopup";

import uniqueIdMixin from "@/lib/mixins/component-unique-id";

export const TABS_THEMES = ["compact"];
export default defineComponent({
	name: "VTabs",

	components: { VPopup, VIcon },
	mixins: [uniqueIdMixin],

	props: {
		tabs: {
			type: Array,
			default: () => [],
		},
		activeTab: {
			type: [String, Number, Object],
			default: null,
		},
		tabIdentity: {
			type: String,
			default: "id",
		},
		tabTitle: {
			type: String,
			default: "name",
		},
		theme: {
			type: String,
			default: null,
			validator: (v) => !v || TABS_THEMES.includes(v),
		},
		minimize: {
			type: Boolean,
			default: false,
		},
		visibleTabsCount: {
			type: Number,
			default: 0,
		},
		popupAlign: {
			type: String,
			default: "left",
		},
	},

	emits: ["update:active-tab", "change"],

	data() {
		return {
			hiddenTabsPopupVisible: false,
		};
	},

	computed: {
		classes() {
			const result = Array.isArray(this.$attrs.classes)
				? [...this.$attrs.classes]
				: [];
			if (this.theme) result.push(this.theme);
			return result;
		},

		hiddenTabsPopupTarget() {
			return `#more_${this.uid}`;
		},

		formattedTabs() {
			return this.tabs.map((item, index) => {
				const tabItem = {
					id: null,
					title: null,
					active: false,
					hidden: false,
				};

				if (typeof item === "string") {
					tabItem.id = index;
					tabItem.title = item;
					tabItem.active = item === this.activeTab;
				} else if (Utils.isObject(item)) {
					tabItem.id = item[this.tabIdentity];
					tabItem.title = item[this.tabTitle];
					tabItem.active =
						Utils.isObject(this.activeTab) &&
						item[this.tabIdentity] === this.activeTab[this.tabIdentity];
				}

				tabItem.hidden = this.minimize && index >= this.visibleTabsCount;
				return tabItem;
			});
		},

		visibleTabs() {
			return this.formattedTabs.filter((item) => !item.hidden);
		},

		hiddenTabs() {
			return this.formattedTabs.filter((item) => item.hidden);
		},

		activeHiddenTab() {
			const index = this.hiddenTabs.findIndex(
				(item) =>
					item.hidden &&
					Utils.isObject(this.activeTab) &&
					item[this.tabIdentity] === this.activeTab[this.tabIdentity]
			);
			return index > -1 ? this.activeTab : null;
		},
	},

	methods: {
		changeTab(tab) {
			this.closeHiddenTabsPopup();

			this.$emit("update:active-tab", tab);
			this.$emit("change", tab);
		},

		openHiddenTabsPopup() {
			this.hiddenTabsPopupVisible = true;
		},

		closeHiddenTabsPopup(event) {
			if (event) {
				const x = event.clientX || event.pageX;
				const y = event.clientY || event.pageY;
				const hoverElement = document.elementFromPoint(x, y);
				const targetElement = this.$el.querySelector(
					this.hiddenTabsPopupTarget
				);

				if (
					!targetElement?.contains(hoverElement) &&
					!this.$refs.hiddenTabsPopup?.$el.contains(hoverElement)
				) {
					this.hiddenTabsPopupVisible = false;
				}
			} else {
				this.hiddenTabsPopupVisible = false;
			}
		},
	},
});
</script>

